import React from 'react';
import './Dashboard.css';
import DashboardStatus from '../DashboardStatus/DashboardStatus';
import { 
  PlaySquareOutlined, 
  UserOutlined, 
  BookOutlined, 
  TeamOutlined,
  DollarOutlined,
  StarOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import { Table } from 'antd';
import { Line, Bar } from '@ant-design/charts';
import CreateClassModal from '../CreateClassModal/CreateClassModal';

function Dashboard() {
  // Status cards data
  const statusData = [
    {
      icon: <PlaySquareOutlined />,
      title: 'Total Classes',
      value: 2341,
      lastWeekValue: 2200,
      iconColor: '#1890ff'
    },
    {
      icon: <UserOutlined />,
      title: 'Students',
      value: 1500,
      lastWeekValue: 1450,
      iconColor: '#722ed1'
    },
    {
      icon: <BookOutlined />,
      title: 'Subscribed Classes',
      value: 1200,
      lastWeekValue: 1100,
      iconColor: '#13c2c2'
    },
    {
      icon: <TeamOutlined />,
      title: 'Teachers',
      value: 50,
      lastWeekValue: 48,
      iconColor: '#fa8c16'
    },
  ];

  // Revenue Analytics Data
  const revenueData = [
    { month: 'Jan', revenue: 120 },
    { month: 'Feb', revenue: 190 },
    { month: 'Mar', revenue: 180 },
    { month: 'Apr', revenue: 210 },
    { month: 'May', revenue: 250 },
    { month: 'Jun', revenue: 220 },
    { month: 'Jul', revenue: 300 },
    { month: 'Aug', revenue: 280 },
    { month: 'Sep', revenue: 350 },
    { month: 'Oct', revenue: 400 },
    { month: 'Nov', revenue: 380 },
    { month: 'Dec', revenue: 420 },
  ];

  const revenueConfig = {
    data: revenueData,
    xField: 'month',
    yField: 'revenue',
    seriesField: 'month',
    color: '#1890ff',
    yAxis: {
      label: {
        formatter: (v) => `${v} Ksh`,
      },
    },
    point: {
      size: 4,
      shape: 'circle',
    },
    smooth: true,
    areaStyle: {
      fill: 'l(270) 0:#ffffff 1:#1890ff',
      fillOpacity: 0.15,
    },
  };

  // Top Videos Data
  const topVideosColumns = [
    {
      title: 'Video Title',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Subject',
      dataIndex: 'subject',
      key: 'subject',
    },
    {
      title: 'Views',
      dataIndex: 'views',
      key: 'views',
      sorter: (a, b) => a.views - b.views,
    },
    {
      title: 'Revenue (Ksh)',
      dataIndex: 'revenue',
      key: 'revenue',
      sorter: (a, b) => a.revenue - b.revenue,
    },
  ];

  const topVideosData = [
    {
      key: '1',
      title: 'Introduction to Algebra',
      subject: 'Mathematics',
      views: 12500,
      revenue: 87500
    },
    {
      key: '2',
      title: 'Organic Chemistry Basics',
      subject: 'Chemistry',
      views: 9800,
      revenue: 68600
    },
    {
      key: '3',
      title: 'English Literature Analysis',
      subject: 'English',
      views: 8700,
      revenue: 60900
    },
    {
      key: '4',
      title: 'Physics: Newton\'s Laws',
      subject: 'Physics',
      views: 7600,
      revenue: 53200
    },
  ];

  // Top Teachers Data
  const topTeachersColumns = [
    {
      title: 'Teacher',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Subject',
      dataIndex: 'subject',
      key: 'subject',
    },
    {
      title: 'Classes',
      dataIndex: 'classes',
      key: 'classes',
      sorter: (a, b) => a.classes - b.classes,
    },
    {
      title: 'Rating',
      dataIndex: 'rating',
      key: 'rating',
      sorter: (a, b) => a.rating - b.rating,
      render: (rating) => `${rating}/5.0`
    },
  ];

  const topTeachersData = [
    {
      key: '1',
      name: 'Mr. Kamau',
      subject: 'Mathematics',
      classes: 42,
      rating: 4.9
    },
    {
      key: '2',
      name: 'Ms. Wanjiku',
      subject: 'Chemistry',
      classes: 38,
      rating: 4.8
    },
    {
      key: '3',
      name: 'Mr. Otieno',
      subject: 'Physics',
      classes: 35,
      rating: 4.7
    },
    {
      key: '4',
      name: 'Ms. Auma',
      subject: 'English',
      classes: 32,
      rating: 4.6
    },
  ];

  // Most Watched Classes by Level Data
  const classLevelData = [
    { level: 'Form 1', views: 45 },
    { level: 'Form 2', views: 38 },
    { level: 'Form 3', views: 52 },
    { level: 'Form 4', views: 60 },
    { level: 'College', views: 25 },
  ];

  const classLevelConfig = {
    data: classLevelData,
    xField: 'views',
    yField: 'level',
    seriesField: 'level',
    color: ['#1890ff', '#722ed1', '#13c2c2', '#fa8c16', '#52c41a'],
    legend: {
      position: 'bottom',
    },
    yAxis: {
      label: {
        autoRotate: false,
      },
    },
    scrollbar: {
      type: 'vertical',
    },
  };

  return (
    <div className="dashboard">
    <div className="dashboard-header">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <span className="dashboard-title">Welcome to the Admin Dashboard</span>
          <p className="dashboard-subtitle">Here you can manage teachers, students, class approvals, payments, and more.</p>
        </div>
        <CreateClassModal />
      </div>
    </div>
      
      {/* Status Cards */}
      <div className="dashboard-status-container">
        {statusData.map((status, index) => (
          <DashboardStatus
            key={index}
            icon={status.icon}
            title={status.title}
            value={status.value}
            lastWeekValue={status.lastWeekValue}
            iconColor={status.iconColor}
          />
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="dashboard-content-grid">
        {/* Revenue Analytics */}
        <div className="dashboard-card">
          <div className="card-header">
            <DollarOutlined className="card-icon" style={{ color: '#1890ff' }} />
            <h3>Revenue Analytics (12 Months)</h3>
          </div>
          <div className="card-content">
            <Line {...revenueConfig} />
          </div>
        </div>

        {/* Top Videos */}
        <div className="dashboard-card">
          <div className="card-header">
            <PlaySquareOutlined className="card-icon" style={{ color: '#722ed1' }} />
            <h3>Top Videos</h3>
          </div>
          <div className="card-content">
            <Table 
              columns={topVideosColumns}
              dataSource={topVideosData}
              pagination={false}
              size="small"
            />
          </div>
        </div>

        {/* Top Teachers */}
        <div className="dashboard-card">
          <div className="card-header">
            <TeamOutlined className="card-icon" style={{ color: '#fa8c16' }} />
            <h3>Top Teachers</h3>
          </div>
          <div className="card-content">
            <Table 
              columns={topTeachersColumns}
              dataSource={topTeachersData}
              pagination={false}
              size="small"
            />
          </div>
        </div>

        {/* Most Watched Classes by Level */}
        <div className="dashboard-card">
          <div className="card-header">
            <BarChartOutlined className="card-icon" style={{ color: '#13c2c2' }} />
            <h3>Most Watched Classes by Level</h3>
          </div>
          <div className="card-content">
            <Bar {...classLevelConfig} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;